import Layout from 'components/layout';

import { lucidColors } from '@lucid/gatsby-theme-lucid';
import { graphql } from 'gatsby';
import { Box, Card, Heading, Paper, VideoPlayer } from 'lucid-components-react';
import React from 'react';
import ReactHtmlParser from 'react-html-parser';

import Footer from 'components/footer/footer';
import Navbar from 'components/navbar/navbar';
import Link from 'components/link/link';
import LucidContainer from 'components/lucid-container/lucid-container';
import VisibleVideo from 'components/visible-video/visible-video';
import {
  Drupal_NodeAddOn,
  Drupal_ParagraphIntegrationSimple,
  Drupal_ParagraphLink,
  Drupal_ParagraphNineteenQuote,
  Drupal_ParagraphSeoImage,
  Drupal_ParagraphSimpleCard,
  Drupal_ParagraphVisibleVideo
} from 'generated/graphql';
import { useMenuDefaultFooter, useMenuTopNavigation } from 'hooks/use-menu';
import { Text } from 'theme/typography';
import { TemplateProps } from 'types/types';
import { getEntityFields } from 'utils/getEntityFields';

const AddOn = ({ data, pageContext, location }: TemplateProps) => {
  const node = data.drupal.nodeById as Drupal_NodeAddOn;

  const { hideSsr, langPrefix, pageType, nid } = pageContext;

  const { entityMetatags, entityTranslations } = getEntityFields(node);

  const navbarMenu = useMenuTopNavigation(langPrefix);
  const footerMenu = useMenuDefaultFooter(langPrefix);

  const title = node.fieldTitlePiece?.length
    ? node.fieldTitlePiece?.filter(piece => piece?.trim()).join('<br />')
    : null;

  const headerImage = node.fieldHeaderImage?.entity as Drupal_ParagraphSeoImage;

  const backNForthSections = node.fieldBackNForthItem
    ?.filter(backNForth => backNForth)
    .map(backNForth => backNForth?.entity as Drupal_ParagraphVisibleVideo);

  const integrations = node.fieldIntegration
    ?.map(
      integration => integration?.entity as Drupal_ParagraphIntegrationSimple
    )
    .filter(
      integration =>
        integration.fieldIntegrationDescription && integration.fieldImageLink
    );

  const featuredVideoButton = (
    <Link
      asButton
      secondary
      color="secondary"
      css={theme => ({
        flex: '1 1 100%',
        [`@media screen and (min-width: ${theme.breakpoints.mobile})`]: {
          flex: '0 1 auto',
          minWidth: '220px'
        }
      })}
      to={node.fieldFeaturedVideoCta?.url?.path!}
    >
      {node.fieldFeaturedVideoCta?.title}
    </Link>
  );

  const quote = node.fieldQuoteBundle?.entity as Drupal_ParagraphNineteenQuote;

  const resources = node.fieldResourceCard
    ?.map(resource => resource?.entity as Drupal_ParagraphSimpleCard)
    .filter(resource => resource?.fieldCardText);

  return (
    <Layout
      entityTranslations={entityTranslations}
      hideSsr={hideSsr}
      langPrefix={langPrefix}
      location={location}
      metatags={entityMetatags}
      nid={nid}
      pageType={pageType}
    >
      <Navbar menu={navbarMenu} />

      <LucidContainer>
        <Box
          className={`${pageType}__hero`}
          display="flex"
          justifyContent="space-between"
          mb={3}
          mr={{ default: '0', tabletSmall: '-60px', tablet: '0' }}
          mt={6}
        >
          <Box
            flex={{
              default: '0 1 100%',
              tabletSmall: '0 1 48%',
              tablet: '0 1 40%'
            }}
          >
            {title && (
              <Heading mb="4" mt="3" variant="headlineS">
                {ReactHtmlParser(title)}
              </Heading>
            )}

            <Text textStyles="subhead">{node.fieldSubtitle}</Text>
            <Box
              css={{
                '& a': {
                  marginRight: '1rem',
                  marginBottom: '1rem'
                }
              }}
              display="flex"
              flexWrap="wrap"
              mt={3}
            >
              {node.fieldHeroCta?.length &&
                node.fieldHeroCta?.map((heroCta, i) => {
                  const entity = heroCta?.entity as Drupal_ParagraphLink;
                  const cta = entity.fieldCta;
                  return (
                    <Link
                      key={cta?.title}
                      asButton
                      css={theme => ({
                        flex: '1 1 100%',
                        [`@media screen and (min-width: ${theme.breakpoints.mobile})`]: {
                          flex: '0 1 auto'
                        }
                      })}
                      primary={i < 1}
                      secondary={i > 0}
                      to={cta?.url?.path!}
                    >
                      {cta?.title}
                    </Link>
                  );
                })}
            </Box>
          </Box>

          <Box
            display={{ default: 'none', tabletSmall: 'block' }}
            flex={{ default: '0 1', tabletSmall: '0 1 45%', tablet: '0 1 55%' }}
            position="relative"
          >
            {headerImage && headerImage.fieldImageLink && (
              <img
                alt={headerImage.fieldAltText || 'header image'}
                css={{
                  position: 'absolute',
                  top: '0',
                  left: '0',
                  height: '100%',
                  maxWidth: 'unset'
                }}
                src={headerImage.fieldImageLink}
              />
            )}
          </Box>
        </Box>

        <Box className={`${pageType}__back-n-forth-heading`} pb={10} pt={9}>
          <Heading textAlign="center" variant="headlineS">
            {node.fieldSectionTitle}
          </Heading>
        </Box>

        {backNForthSections?.map(
          ({ fieldSectionTitle, fieldSectionText, fieldVideoLink }, i) => {
            return (
              <Box
                key={fieldSectionTitle || i}
                alignItems="center"
                className={`${pageType}__back-n-forth-item`}
                display="flex"
                flexDirection={i % 2 ? 'row-reverse' : 'row'}
                flexWrap="wrap"
                justifyContent="space-between"
                mb="10"
              >
                <Box
                  css={theme => ({
                    flex: '0 1 100%',
                    [`@media screen and (min-width: ${theme.breakpoints.tabletSmall})`]: {
                      flex: '0 1 53%'
                    }
                  })}
                  mb="3"
                >
                  {fieldVideoLink && (
                    <VisibleVideo
                      src={fieldVideoLink}
                      title={fieldSectionTitle}
                    />
                  )}
                </Box>
                <Box
                  css={theme => ({
                    flex: '0 1 100%',
                    [`@media screen and (min-width: ${theme.breakpoints.tabletSmall})`]: {
                      flex: '0 1 40%'
                    }
                  })}
                >
                  <Heading as="h3" mb="4" variant="subheadS">
                    {fieldSectionTitle}
                  </Heading>
                  <Text textStyles="body">{fieldSectionText}</Text>
                </Box>
              </Box>
            );
          }
        )}

        {integrations && integrations.length > 0 && (
          <Box className={`${pageType}__integrations`} my="10">
            <Heading as="h2" mb="7" variant="subheadM">
              {node.fieldIntegrationSectionTitle}
            </Heading>

            <Box
              className={`${pageType}__integration-list`}
              display="flex"
              flexWrap="wrap"
              justifyContent="space-between"
            >
              {integrations?.map(
                ({
                  fieldIntegrationDescription,
                  fieldIntegrationUrl,
                  fieldImageLink,
                  fieldImageAlt
                }) => {
                  return fieldIntegrationUrl?.url?.path ? (
                    <Link
                      key={fieldIntegrationUrl.url.path}
                      css={theme => ({
                        flex: '0 1 100%',
                        marginBottom: '3rem',
                        [`@media screen and (min-width: ${theme.breakpoints.mobile})`]: {
                          flex: '0 1 47%'
                        },
                        [`@media screen and (min-width: ${theme.breakpoints.tablet})`]: {
                          marginBottom: '0'
                        }
                      })}
                      to={fieldIntegrationUrl.url.path}
                      underline="none"
                    >
                      <Card height="100%" padding="4" variant="interactive">
                        <Box
                          alignItems={{
                            default: 'flex-end',
                            tabletLandscape: 'center'
                          }}
                          display="flex"
                          flexWrap="wrap"
                          justifyContent="space-between"
                        >
                          <Box
                            css={theme => ({
                              flex: '1 1 100%',
                              textAlign: 'center',
                              [`@media screen and (min-width: ${theme.breakpoints.tabletLandscape})`]: {
                                flex: '0 1 32%'
                              }
                            })}
                          >
                            {fieldImageLink && (
                              <img
                                alt={fieldImageAlt || 'integration logo'}
                                css={theme => ({
                                  marginBottom: '2rem',
                                  maxHeight: '100px',
                                  [`@media screen and (min-width: ${theme.breakpoints.tabletLandscape})`]: {
                                    marginBottom: '0'
                                  }
                                })}
                                src={fieldImageLink}
                              />
                            )}
                          </Box>
                          <Box
                            css={theme => ({
                              flex: '1 1 100%',
                              [`@media screen and (min-width: ${theme.breakpoints.tabletLandscape})`]: {
                                flex: '0 1 65%'
                              }
                            })}
                            display="flex"
                            flexDirection="column"
                          >
                            <Text
                              css={{
                                flex: '1 0 auto',
                                margin: `${
                                  fieldIntegrationUrl?.title ? '0 0 1rem 0' : 0
                                  }`
                              }}
                              textStyles="card"
                            >
                              {fieldIntegrationDescription}
                            </Text>
                            {fieldIntegrationUrl?.title && (
                              <Text css={{ margin: 0 }} textStyles="link">
                                {fieldIntegrationUrl?.title}
                              </Text>
                            )}
                          </Box>
                        </Box>
                      </Card>
                    </Link>
                  ) : (
                      /* eslint-disable-next-line react/jsx-indent */
                      <Box
                        css={theme => ({
                          flex: '0 1 100%',
                          marginBottom: '3rem',
                          [`@media screen and (min-width: ${theme.breakpoints.mobile})`]: {
                            flex: '0 1 47%'
                          },
                          [`@media screen and (min-width: ${theme.breakpoints.tablet})`]: {
                            marginBottom: '1.5rem'
                          }
                        })}
                      >
                        <Card height="100%" padding="4">
                          <Box
                            alignItems={{
                              default: 'flex-end',
                              tabletLandscape: 'center'
                            }}
                            display="flex"
                            flexWrap="wrap"
                            justifyContent="space-between"
                          >
                            <Box
                              css={theme => ({
                                flex: '1 1 100%',
                                textAlign: 'center',
                                [`@media screen and (min-width: ${theme.breakpoints.tabletLandscape})`]: {
                                  flex: '0 1 32%'
                                }
                              })}
                            >
                              {fieldImageLink && (
                                <img
                                  alt={fieldImageAlt || 'integration logo'}
                                  css={theme => ({
                                    marginBottom: '2rem',
                                    maxHeight: '100px',
                                    [`@media screen and (min-width: ${theme.breakpoints.tabletLandscape})`]: {
                                      marginBottom: '0'
                                    }
                                  })}
                                  src={fieldImageLink}
                                />
                              )}
                            </Box>
                            <Box
                              css={theme => ({
                                flex: '1 1 100%',
                                [`@media screen and (min-width: ${theme.breakpoints.tabletLandscape})`]: {
                                  flex: '0 1 65%'
                                }
                              })}
                              display="flex"
                              flexDirection="column"
                            >
                              <Text
                                css={{
                                  flex: '1 0 auto',
                                  margin: `${
                                    fieldIntegrationUrl?.title ? '0 0 1rem 0' : 0
                                    }`
                                }}
                                textStyles="card"
                              >
                                {fieldIntegrationDescription}
                              </Text>
                              {fieldIntegrationUrl?.title && (
                                <Text css={{ margin: 0 }} textStyles="link">
                                  {fieldIntegrationUrl?.title}
                                </Text>
                              )}
                            </Box>
                          </Box>
                        </Card>
                      </Box>
                    );
                }
              )}
            </Box>
          </Box>
        )}
      </LucidContainer>

      {node.fieldFeaturedVideoTitle && node.fieldFeaturedVideoLink && (
        <Box
          className={`${pageType}__featuredVideo`}
          css={theme => ({
            background: `linear-gradient(${lucidColors.coolGrey[100]} 60%, transparent 60%)`,
            [`@media screen and (min-width: ${theme.breakpoints.tabletLandscape})`]: {
              background: `linear-gradient(90deg, ${lucidColors.coolGrey[100]} 47.5%, transparent 47.5%)`
            }
          })}
          my={6}
          py={6}
        >
          <LucidContainer>
            <Box
              alignItems="center"
              display="flex"
              flexWrap="wrap"
              justifyContent="space-between"
            >
              <Box
                css={theme => ({
                  flex: '1 1 100%',
                  [`@media screen and (min-width: ${theme.breakpoints.tabletLandscape})`]: {
                    flex: '0 1 35%'
                  }
                })}
              >
                <Heading as="h2" color="white" mb="5" variant="subheadM">
                  {node.fieldFeaturedVideoTitle}
                </Heading>
                <ul
                  style={{
                    marginBottom: '2rem',
                    marginLeft: 0,
                    listStylePosition: 'inside',
                    display: 'flex',
                    flexWrap: 'wrap'
                  }}
                >
                  {node.fieldFeaturedVideoList?.map((item, index) => (
                    <li
                      key={`li-item-${index}`}
                      css={theme => ({
                        marginBottom: '1rem',
                        color: lucidColors.white,
                        flex: '0 1 100%',
                        [`@media screen and (min-width: ${theme.breakpoints.tabletSmall})`]: {
                          flex: '0 1 50%'
                        },
                        [`@media screen and (min-width: ${theme.breakpoints.tabletLandscape})`]: {
                          flex: '0 1 100%'
                        }
                      })}
                    >
                      {item}
                    </li>
                  ))}
                </ul>
                <Box
                  css={theme => ({
                    display: 'none',
                    [`@media screen and (min-width: ${theme.breakpoints.tabletLandscape})`]: {
                      display: 'block'
                    }
                  })}
                >
                  {featuredVideoButton}
                </Box>
              </Box>

              <Box
                css={theme => ({
                  flex: '1 1 100%',
                  [`@media screen and (min-width: ${theme.breakpoints.tabletLandscape})`]: {
                    flex: '0 1 60%'
                  }
                })}
              >
                {node.fieldFeaturedVideoLink && (
                  <VideoPlayer
                    controls
                    placeholder={() => (
                      <img
                        alt="featured video placeholder"
                        src={node.fieldFeaturedVideoPlaceholder!}
                      />
                    )}
                    playing={false}
                    url={node.fieldFeaturedVideoLink}
                  />
                )}

                <Box
                  css={theme => ({
                    display: 'flex',
                    justifyContent: 'flex-end',
                    [`@media screen and (min-width: ${theme.breakpoints.tabletLandscape})`]: {
                      display: 'none'
                    }
                  })}
                  mt="7"
                >
                  {featuredVideoButton}
                </Box>
              </Box>
            </Box>
          </LucidContainer>
        </Box>
      )}

      {quote.fieldQuote && (
        <LucidContainer className={`${pageType}__quote`}>
          <Box
            alignItems="center"
            display="flex"
            flexWrap="wrap"
            justifyContent="space-between"
            my={{ default: 6, tablet: 10 }}
          >
            <Box
              flex={{ default: '1 1 100%', tabletSmall: '0 1 25%' }}
              mb={{ default: 5, tabletLandscape: 0 }}
            >
              <img
                alt="quote brand logo"
                css={{ maxHeight: '75px' }}
                src={quote.fieldImage!}
              />
            </Box>
            <Box flex={{ default: '1 1 100%', tabletSmall: '0 1 70%' }}>
              <Text textStyles="quoteL">{quote.fieldQuote}</Text>
              <Box mt={3}>
                <p css={theme => ({ fontWeight: theme.fontWeights.bold })}>
                  {quote.fieldAttribution}
                </p>
              </Box>
            </Box>
          </Box>
        </LucidContainer>
      )}

      {resources && resources.length > 0 && (
        <Box
          style={{
            background: lucidColors.coolGrey[100]
          }}
        >
          <LucidContainer className={`${pageType}__resources`}>
            <Box py={7}>
              <Heading as="h2" color="white" mb="5" variant="subheadM">
                {node.fieldResourcesSectionTitle}
              </Heading>

              <Box
                display="flex"
                flexWrap="wrap"
                justifyContent="space-between"
              >
                {resources?.map(
                  (
                    {
                      fieldImageLink,
                      fieldImageAlt,
                      fieldCardText,
                      fieldCardLink
                    },
                    i
                  ) => (
                      /* eslint-disable-next-line react/jsx-indent */
                      <Link
                        key={i}
                        css={theme => ({
                          flex: '0 1 100%',
                          marginBottom: '2rem',
                          [`@media screen and (min-width: ${theme.breakpoints.mobile})`]: {
                            flex: '0 1 48%'
                          },
                          [`@media screen and (min-width: ${theme.breakpoints.tablet})`]: {
                            flex: '0 1 22%',
                            marginBottom: '0'
                          }
                        })}
                        to={fieldCardLink?.url?.path!}
                        underline="none"
                      >
                        <Card
                          css={{
                            flexDirection: 'column',
                            height: '100%'
                          }}
                          padding="4"
                          variant="interactive"
                        >
                          <Card.Image css={{ flex: '0 1' }}>
                            {fieldImageLink && (
                              <img
                                alt={fieldImageAlt || 'resource link'}
                                css={{ width: '100%' }}
                                src={fieldImageLink}
                              />
                            )}
                          </Card.Image>
                          <Card.Content css={{ flex: '1 1 auto' }}>
                            <p
                              css={theme => ({
                                flex: '1 1 auto',
                                fontWeight: theme.fontWeights.bold
                              })}
                            >
                              {fieldCardText}
                            </p>
                            <p
                              css={theme => ({
                                color: theme.colors.primary,
                                margin: '0',
                                textAlign: 'right',
                                fontWeight: theme.fontWeights.bold
                              })}
                            >
                              {fieldCardLink?.title}
                            </p>
                          </Card.Content>
                        </Card>
                      </Link>
                    )
                )}
              </Box>
            </Box>
          </LucidContainer>
        </Box>
      )}

      {node.fieldPreFooterCtaText && (
        <Box
          className={`${pageType}__pre-footer`}
          css={{
            background: `linear-gradient(${lucidColors.coolGrey[100]} 50%, transparent 50%)`
          }}
          py="10"
        >
          <LucidContainer flex>
            <Paper
              css={{
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                flexWrap: 'wrap'
              }}
            >
              <Heading
                as="h2"
                css={(theme: any) => ({
                  flex: '0 1 100%',
                  [`@media screen and (min-width: ${theme.breakpoints.tabletSmall})`]: {
                    flex: '1 1',
                    marginRight: '56px'
                  },
                  [`@media screen and (min-width: ${theme.breakpoints.tablet})`]: {
                    flex: '1 1',
                    marginRight: '84px'
                  }
                })}
                m="0"
                variant="subheadM"
              >
                {node.fieldPreFooterCtaText}
              </Heading>
              <Box
                display="flex"
                flex={{ default: '0 1 100%', tabletSmall: '0 1' }}
                justifyContent="flex-end"
                mt={{ default: '2rem', tabletSmall: '0' }}
              >
                <Link
                  asButton
                  primary
                  css={theme => ({
                    flex: '1 1 100%',
                    [`@media screen and (min-width: ${theme.breakpoints.mobile})`]: {
                      flex: '0 1 auto',
                      minWidth: '220px'
                    }
                  })}
                  to={node?.fieldPreFooterCta?.url?.path!}
                >
                  {node.fieldPreFooterCta?.title}
                </Link>
              </Box>
            </Paper>
          </LucidContainer>
        </Box>
      )}
      <Footer
        entityTranslations={entityTranslations}
        menu={footerMenu}
      />
    </Layout>
  );
};

export default AddOn;

export const query = graphql`
  query($nid: String!, $LanguageId: Drupal_LanguageId!) {
    drupal {
      nodeById(id: $nid, language: $LanguageId) {
        ... on Drupal_NodeAddOn {
          ...entityFields
          fieldTitlePiece
          fieldSubtitle
          fieldHeroCta {
            entity {
              ...link
            }
          }
          fieldHeaderImage {
            entity {
              ... on Drupal_ParagraphSeoImage {
                fieldImageLink
                fieldAltText
              }
            }
          }
          fieldSectionTitle
          fieldBackNForthItem {
            entity {
              ... on Drupal_ParagraphVisibleVideo {
                fieldSectionTitle
                fieldSectionText
                fieldVideoLink
              }
            }
          }
          fieldIntegrationSectionTitle
          fieldIntegration {
            entity {
              ... on Drupal_ParagraphIntegrationSimple {
                fieldIntegrationDescription
                fieldImageLink
                fieldImageAlt
                fieldIntegrationUrl {
                  title
                  url {
                    path
                  }
                }
              }
            }
          }
          fieldFeaturedVideoTitle
          fieldFeaturedVideoList
          fieldFeaturedVideoCta {
            title
            url {
              path
            }
          }
          fieldFeaturedVideoLink
          fieldFeaturedVideoPlaceholder
          fieldQuoteBundle {
            entity {
              ...nineteenQuote
            }
          }
          fieldResourcesSectionTitle
          fieldResourceCard {
            entity {
              ... on Drupal_ParagraphSimpleCard {
                fieldImageLink
                fieldImageAlt
                fieldCardText
                fieldCardLink {
                  url {
                    path
                  }
                  title
                }
              }
            }
          }
          fieldPreFooterCtaText
          fieldPreFooterCta {
            title
            url {
              path
            }
          }
        }
      }
    }
  }
`;
